import type { Tour } from "./types";

/**
 * Semantic tours — three pages: Conflicts (where the variants live),
 * Trusted catalog (where the canonical lives), Drift (where the
 * surprises surface). Each tour articulates how Semantic prevents
 * "MRR means three different things in three reports".
 */

const NAV = (path: string) => `nav[data-tour-id="nav"] a[href="${path}"]`;

export const conflictsTour: Tour = {
  id: "semantic.conflicts.v1",
  title: "Welcome to Semantic",
  subtitle: "One trusted definition per metric, across every BI platform.",
  steps: [
    {
      placement: "center",
      title: "Same metric, three definitions",
      body:
        "MRR in Finance's Cognos report. MRR in Sales' Power BI dashboard. MRR in IR's Tableau viz. Often: three different formulas. Semantic detects these conflicts, recommends the canonical, and propagates back to source platforms.",
    },
    {
      target: '[data-tour-id="conflicts-list"]',
      placement: "top",
      title: "Conflict clusters",
      body:
        "Each card is a cluster — definitions that look like the same metric across platforms but disagree on the formula. Variants are listed with platform, owner, and the actual formula side-by-side.",
    },
    {
      target: '[data-tour-id="conflicts-recommend"]',
      placement: "left",
      title: "Recommended canonical",
      body:
        "Semantic's recommendation engine ranks variants on six signals — lineage primacy, ownership, dbt presence, usage, validity, glossary alignment — and suggests one as canonical. The reasoning is shown so you can override.",
    },
    {
      target: '[data-tour-id="conflicts-designate"]',
      placement: "left",
      title: "Designate as canonical",
      body:
        "When you designate a definition, it's promoted into the Trusted catalog with full approval history. Re-designations bump the version; the audit trail compounds.",
    },
    {
      target: NAV("/trusted"),
      placement: "bottom",
      title: "Trusted catalog",
      body:
        "Every designation lands here as the canonical truth. Operate, Curate, Bridge, and Atlas can lookup the canonical at runtime; downstream readers see one definition, not three.",
    },
  ],
};

export const trustedTour: Tour = {
  id: "semantic.trusted.v1",
  title: "Trusted catalog",
  subtitle: "Every canonical metric, with full approval history.",
  steps: [
    {
      placement: "center",
      title: "The single source of truth",
      body:
        "Each row is a trusted definition — name, IR formula, owner, status, version. The audit history is append-only; nothing is ever lost.",
    },
    {
      target: '[data-tour-id="trusted-list"]',
      placement: "top",
      title: "Inspect any definition",
      body:
        "Click a row to expand the full approval history: who designated, when, why. Bumping versions is one click — Semantic preserves prior versions so reverts are non-destructive.",
    },
    {
      target: NAV("/drift"),
      placement: "bottom",
      title: "Drift detection",
      body:
        "Once a definition is canonical, Semantic watches for source-platform drift. If someone edits Power BI's MRR formula out-of-band, you'll see it in the Drift tab before it ships to a stakeholder report.",
    },
  ],
};

export const driftTour: Tour = {
  id: "semantic.drift.v1",
  title: "Drift",
  subtitle: "Catch out-of-band changes before they ship.",
  steps: [
    {
      placement: "center",
      title: "Continuous metric integrity",
      body:
        "Semantic re-extracts source platforms on schedule and compares against the trusted catalog. Out-of-band edits, ownership changes, dbt drift, and new conflicts surface here — severity-tagged for triage.",
    },
    {
      target: '[data-tour-id="drift-signals"]',
      placement: "top",
      title: "Severity-tiered signals",
      body:
        "CRITICAL (canonical was modified post-trust), MAJOR (a new conflict appeared), MINOR (ownership changed). Each signal carries enough context to either approve as a re-designation or revert at the source.",
    },
    {
      target: '[data-tour-id="drift-snapshot"]',
      placement: "left",
      title: "Manual snapshot",
      body:
        "Take a snapshot any time to capture the current state of all trusted definitions for forensic comparison later. Snapshots are bounded to 30 per tenant — the rolling window covers a full quarter at weekly cadence.",
    },
  ],
};

export function tourForPath(pathname: string): Tour | undefined {
  if (pathname.startsWith("/conflicts")) return conflictsTour;
  if (pathname.startsWith("/trusted")) return trustedTour;
  if (pathname.startsWith("/drift")) return driftTour;
  return undefined;
}
